// Toast on new incoming shared estimates. Reads from the same React Query
// cache as AlertBell (useIncomingSharedEstimates) so the two sources can't
// diverge. Per-uid seen-set in localStorage (cap 200) prevents re-toasting
// the same doc across polls or reloads.
//
// listMySharedEstimates only returns PENDING docs, so every ID is by
// definition unacted — first-ever load toasts them (no silent-seed).

import { useEffect } from 'react'
import { useAuth } from './useAuth'
import { toastOk } from '../lib/toastStore'
import { useIncomingSharedEstimates } from './useIncomingSharedEstimates'

const CAP = 200

function keyFor(uid: string): string {
  return `jsp.incomingEstSeen.${uid}`
}

function readSeen(uid: string): string[] {
  try {
    const raw = localStorage.getItem(keyFor(uid))
    if (raw === null) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function writeSeen(uid: string, ids: string[]): void {
  try {
    const trimmed = ids.length > CAP ? ids.slice(ids.length - CAP) : ids
    localStorage.setItem(keyFor(uid), JSON.stringify(trimmed))
  } catch { /* quota — ignore */ }
}

export function useIncomingEstimateAlerts(): void {
  const { effectiveUid } = useAuth()
  const { data } = useIncomingSharedEstimates()

  useEffect(() => {
    if (!effectiveUid || !data) return
    const uid = effectiveUid
    const prior = readSeen(uid)
    const seen = new Set(prior)
    const fresh = data.filter((d) => !seen.has(d.id))
    if (fresh.length === 0) return
    for (const d of fresh) {
      const who = d.fromName || d.fromTeamCode || 'a contractor'
      toastOk(`📥 New estimate from ${who}`)
      seen.add(d.id)
    }
    writeSeen(uid, [...prior, ...fresh.map((d) => d.id)])
  }, [effectiveUid, data])
}
